// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — every colour that carries meaning, in one place so it can be MEASURED.
//
// A colour literal inlined in a renderer is a colour nothing measures, and that is exactly how an
// invisible game piece ships: a sibling game drew its walls at 1.14:1 against the board, in the right
// cells at the right size, completely invisible, and the screenshot looked atmospheric.
// tests/contrast.test.ts holds every entry here to WCAG 2.1 §1.4.11's 3:1 floor against every
// surface it can actually be drawn on.
//
// COLOUR IS NEVER LOAD-BEARING HERE. A card is identified by a printed DIGIT and its full name, in
// every place it appears — hand, docket, discard row, results table. The tier tints below are
// separated by BRIGHTNESS rather than hue, so they survive any colour blindness and a greyscale
// screenshot, and they are decoration on top of text that already says everything.

export const PALETTE = {
  /** The page behind everything — a dark panelled room. */
  bg: '#14161c',
  /** Cards and the docket rows: the surface most marks sit on. */
  surface: '#232733',
  /** Raised panels: sheets, the action bar, the gallery strip. */
  panel: '#2c3140',
  /** Hairlines and inactive tracks — decoration, sitting on a panel rather than on the page. */
  line: '#454c60',
  /**
   * The boundary of a CONTROL: cards, rank chips, rival buttons, segmented buttons.
   *
   * Held to the 3:1 graphic floor deliberately. A card's fill is barely above its background, so the
   * border carries almost the whole "this is a tappable thing" signal — which is precisely the case
   * WCAG sets that floor for, and precisely the thing a screenshot cannot judge.
   */
  edge: '#7f88a6',
  /** Body text. */
  ink: '#eef1f7',
  /** Secondary text. Held to the 4.5:1 TEXT floor, because it is text, not a graphic. */
  dim: '#a7b0c4',
  /** The accent: standing, the seal, anything banked. */
  brass: '#e6b955',
  /** Danger — put out, the Verdict, a call that missed. */
  warn: '#f2795e',
  /** Confirmation — survived, backed correctly, a call that landed. */
  good: '#5fd39b',
  /** Under Recusal: off the record, untouchable. */
  shield: '#7fb7f5',
} as const;

/**
 * The rank tint, in three bands by brightness rather than hue.
 *
 * Eight hues that must EACH clear 3:1 on a dark ground are all forced into the light end of the
 * range, which leaves them a fraction of a stop apart — measurable as "distinct", useless in a
 * glance, and invisible to a colour-blind player. Three brightness bands are legible at arm's
 * length on a phone, and the digit on the card is what actually names it.
 */
export const TIER_COLOR = ['#8d95ab', '#c3cbdd', '#f4f6fb'];
export const tierOfRank = (rank: number): number => (rank <= 3 ? 0 : rank <= 6 ? 1 : 2);

/** Every surface a meaningful mark can be drawn on. The contrast test crosses these with the marks. */
export const SURFACES = [PALETTE.bg, PALETTE.surface, PALETTE.panel] as const;

/** Everything that carries meaning on the play surface, by name, for the contrast test to enumerate. */
export const MEANINGFUL: Record<string, string> = {
  brass: PALETTE.brass,
  warn: PALETTE.warn,
  good: PALETTE.good,
  shield: PALETTE.shield,
  ink: PALETTE.ink,
  dim: PALETTE.dim,
  edge: PALETTE.edge,
  tierLow: TIER_COLOR[0],
  tierMid: TIER_COLOR[1],
  tierHigh: TIER_COLOR[2],
};

// ── contrast maths, shared by the test and by nothing else at runtime ───────────────────────────

export function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
